// CR075-P3: Normalized Deals View
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  Search, ChevronDown, Download, AlertTriangle, CheckCircle2, 
  DollarSign, Calendar, Tag, ExternalLink 
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface NormalizedDeal {
  schema_version: string;
  merchant_key: string;
  merchant_name: string;
  deal_type: string;
  headline_mechanic: string;
  // CR133: Semantic classification fields for proper display
  benefit_type?: string | null;
  payout_method?: string | null;
  raw_benefit_text?: string | null;
  value: {
    computed_value_cad: number | null;
    effective_percent_back: number | null;
    pay_amount_cad: number | null;
    receive_value_cad: number | null;
    bonus_value_cad: number | null;
    discount_percent: number | null;
    qualifying_spend_cad: number | null;
  };
  brands: {
    purchase_brand: string | null;
    bonus_brand: string | null;
  };
  dates: {
    valid_from: string | null;
    valid_until: string | null;
  };
  sources: Array<{
    source_type: string;
    source_url: string | null;
    deal_link: string | null;
    staging_item_id: string;
  }>;
  dedupe_fingerprint_inputs: {
    merchant_key: string;
    purchase_brand: string | null;
    headline_normalized: string;
    value_key: string;
    valid_until: string | null;
    // CR132: Added for dedup economics matching
    benefit_type?: string | null;
    payout_method?: string | null;
  };
  publish: {
    is_publishable: boolean;
    missing_fields: string[];
    warnings: string[];
  };
  raw_offer_text: string | null;
  claim_url: string | null;
  confidence: number | null;
}

interface NormalizedRow {
  id: string;
  batch_run_id: string;
  merchant_key: string;
  normalized_deal_json: NormalizedDeal;
  staging_item_id: string | null;
  computed_value_cad: number | null;
  effective_percent_back: number | null;
  normalization_warnings: string[] | null;
  created_at: string;
}

interface NormalizedDealsViewProps {
  batchRunId: string;
}

// CR150: S4 step config includes invocation tracking
// CR210: Extended with fingerprint verification counters
interface S4StepConfig {
  invocation_id?: string;
  invocation_completed_at?: string;
  records_deleted?: number;
  records_inserted?: number;
  // CR210: Fingerprint verification counters from normalize_diagnostics.counts
  normalize_diagnostics?: {
    counts?: {
      fingerprinted_discount_count?: number;
      fingerprinted_bonus_count?: number;
      low_quality_fingerprint_count?: number;
      spend_and_earn_flagged_count?: number;
    };
  };
}

export function NormalizedDealsView({ batchRunId }: NormalizedDealsViewProps) {
  const [deals, setDeals] = useState<NormalizedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // CR150: Fetch invocation info from S4 step config_json
  const [s4InvocationInfo, setS4InvocationInfo] = useState<S4StepConfig | null>(null);

  useEffect(() => {
    fetchDeals();
    fetchS4InvocationInfo();
  }, [batchRunId]);

  // CR150: Fetch the S4 step's config_json to get invocation_id
  const fetchS4InvocationInfo = async () => {
    try {
      const { data } = await supabase
        .from("cr075_batch_run_steps")
        .select("config_json")
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S4")
        .single();
      
      if (data?.config_json) {
        setS4InvocationInfo(data.config_json as S4StepConfig);
      }
    } catch (err) {
      console.error("Failed to fetch S4 invocation info:", err);
    }
  };

  const fetchDeals = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("cr075_staging_deals_normalized")
        .select("*")
        .eq("batch_run_id", batchRunId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setDeals((data || []) as unknown as NormalizedRow[]);
    } catch (err) {
      console.error("Failed to fetch normalized deals:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const filteredDeals = deals.filter(d => {
    if (!searchTerm) return true;
    const deal = d.normalized_deal_json;
    const search = searchTerm.toLowerCase();
    return (
      deal.merchant_name.toLowerCase().includes(search) ||
      deal.headline_mechanic.toLowerCase().includes(search) ||
      (deal.brands.purchase_brand?.toLowerCase().includes(search) ?? false)
    );
  });

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(deals, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `normalized_deals_${batchRunId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // CR133: Format value with proper semantic display for bonus cards
  const formatValue = (deal: NormalizedDeal): string => {
    if (deal.value.computed_value_cad) {
      return `$${deal.value.computed_value_cad.toFixed(2)}`;
    }
    if (deal.value.effective_percent_back) {
      // CR133: Display "bonus cards" for BONUS_CARD_PERCENT, not just "%"
      if (deal.benefit_type === "BONUS_CARD_PERCENT") {
        return `${deal.value.effective_percent_back}% bonus cards`;
      }
      if (deal.benefit_type === "DISCOUNT_PERCENT") {
        return `${deal.value.effective_percent_back}% off`;
      }
      if (deal.benefit_type === "CASHBACK_PERCENT") {
        return `${deal.value.effective_percent_back}% back`;
      }
      // Default percent display
      return `${deal.value.effective_percent_back}%`;
    }
    if (deal.value.pay_amount_cad && deal.value.receive_value_cad) {
      return `Pay $${deal.value.pay_amount_cad} → Get $${deal.value.receive_value_cad}`;
    }
    return "No value";
  };

  // Count unique staging_item_ids to detect duplicates
  const uniqueStagingIds = new Set(deals.map(d => d.staging_item_id).filter(Boolean));
  const hasDuplicates = uniqueStagingIds.size < deals.length && deals.length > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Normalized Deals (S4)
              <Badge variant="outline">{deals.length}</Badge>
              {hasDuplicates && (
                <Badge variant="destructive" className="text-xs">
                  {deals.length - uniqueStagingIds.size} duplicates detected
                </Badge>
              )}
              {/* CR150: Show invocation info from S4 step config */}
              {s4InvocationInfo?.invocation_id && (
                <Badge variant="outline" className="text-xs font-mono" title={`Completed: ${s4InvocationInfo.invocation_completed_at || 'unknown'}`}>
                  inv: {s4InvocationInfo.invocation_id}
                </Badge>
              )}
              {s4InvocationInfo?.records_deleted !== undefined && s4InvocationInfo.records_deleted > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {s4InvocationInfo.records_deleted} deleted → {s4InvocationInfo.records_inserted} inserted
                </Badge>
              )}
            </CardTitle>
            {/* CR210: Show fingerprint verification counters */}
            {s4InvocationInfo?.normalize_diagnostics?.counts && (
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                <span className="font-medium">CR210:</span>
                {s4InvocationInfo.normalize_diagnostics.counts.fingerprinted_discount_count !== undefined && (
                  <Badge variant="outline" className="text-xs">
                    DISCOUNT: {s4InvocationInfo.normalize_diagnostics.counts.fingerprinted_discount_count}
                  </Badge>
                )}
                {s4InvocationInfo.normalize_diagnostics.counts.fingerprinted_bonus_count !== undefined && (
                  <Badge variant="outline" className="text-xs">
                    BONUS: {s4InvocationInfo.normalize_diagnostics.counts.fingerprinted_bonus_count}
                  </Badge>
                )}
                {s4InvocationInfo.normalize_diagnostics.counts.low_quality_fingerprint_count !== undefined && 
                 s4InvocationInfo.normalize_diagnostics.counts.low_quality_fingerprint_count > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    LOW_QUALITY: {s4InvocationInfo.normalize_diagnostics.counts.low_quality_fingerprint_count}
                  </Badge>
                )}
                {s4InvocationInfo.normalize_diagnostics.counts.spend_and_earn_flagged_count !== undefined && 
                 s4InvocationInfo.normalize_diagnostics.counts.spend_and_earn_flagged_count > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    SPEND_AND_EARN: {s4InvocationInfo.normalize_diagnostics.counts.spend_and_earn_flagged_count}
                  </Badge>
                )}
              </div>
            )}
            <CardDescription>
              Canonical schema view of staged deals
              {uniqueStagingIds.size !== deals.length && deals.length > 0 && (
                <span className="text-destructive ml-2">
                  ({uniqueStagingIds.size} unique staging items)
                </span>
              )}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={exportJson} className="gap-2">
            <Download className="h-4 w-4" />
            Export JSON
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by merchant, headline, or brand..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : filteredDeals.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No normalized deals found</div>
        ) : (
          <ScrollArea className="h-[500px]">
            <div className="space-y-3">
              {filteredDeals.map((row) => {
                const deal = row.normalized_deal_json;
                const isExpanded = expandedIds.has(row.id);

                return (
                  <Collapsible key={row.id} open={isExpanded} onOpenChange={() => toggleExpand(row.id)}>
                    <div className="border rounded-lg">
                      <CollapsibleTrigger asChild>
                        <button className="w-full p-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{deal.merchant_name}</span>
                              <Badge variant="outline" className="text-xs">{deal.deal_type}</Badge>
                              {deal.publish.is_publishable ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                              ) : (
                                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground truncate">
                              {deal.headline_mechanic}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="font-medium text-primary">{formatValue(deal)}</div>
                              <div className="text-xs text-muted-foreground">
                                {deal.sources[0]?.source_type}
                              </div>
                            </div>
                            <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                          </div>
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="px-4 pb-4 pt-2 border-t space-y-4">
                          {/* Value Block */}
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div className="flex items-center gap-2">
                              <DollarSign className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <div className="text-muted-foreground">Computed Value</div>
                                <div className="font-medium">
                                  {deal.value.computed_value_cad ? `$${deal.value.computed_value_cad}` : "-"}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <DollarSign className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <div className="text-muted-foreground">Effective %</div>
                                <div className="font-medium">
                                  {deal.value.effective_percent_back ? `${deal.value.effective_percent_back}%` : "-"}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <div className="text-muted-foreground">Valid Until</div>
                                <div className="font-medium">{deal.dates.valid_until || "-"}</div>
                              </div>
                            </div>
                          </div>

                          {/* Brands Block */}
                          {(deal.brands.purchase_brand || deal.brands.bonus_brand) && (
                            <div className="flex items-center gap-2">
                              <Tag className="h-4 w-4 text-muted-foreground" />
                              {deal.brands.purchase_brand && (
                                <Badge variant="secondary">{deal.brands.purchase_brand}</Badge>
                              )}
                              {deal.brands.bonus_brand && (
                                <Badge variant="outline">{deal.brands.bonus_brand}</Badge>
                              )}
                            </div>
                          )}

                          {/* Dedupe Fingerprint */}
                          <div className="p-3 bg-muted/50 rounded text-xs font-mono">
                            <div className="text-muted-foreground mb-1">Dedupe Fingerprint Inputs:</div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>merchant: {deal.dedupe_fingerprint_inputs.merchant_key}</div>
                              {/* CR187: Highlight purchase_brand when present - key for gift card dedup */}
                              <div className={deal.dedupe_fingerprint_inputs.purchase_brand ? "text-purple-600 dark:text-purple-400 font-semibold" : ""}>
                                brand: {deal.dedupe_fingerprint_inputs.purchase_brand || <span className="text-muted-foreground">none</span>}
                              </div>
                              <div>headline: {deal.dedupe_fingerprint_inputs.headline_normalized.slice(0, 30)}...</div>
                              <div>value: {deal.dedupe_fingerprint_inputs.value_key}</div>
                            </div>
                          </div>

                          {/* Publish Status */}
                          <div className="flex items-start gap-2">
                            {deal.publish.is_publishable ? (
                              <Badge className="bg-green-500">Publishable</Badge>
                            ) : (
                              <Badge variant="destructive">Not Publishable</Badge>
                            )}
                            {deal.publish.warnings.map((w, i) => (
                              <Badge key={i} variant="outline" className="text-yellow-600">{w}</Badge>
                            ))}
                          </div>

                          {/* Claim URL */}
                          {deal.claim_url && (
                            <Button variant="outline" size="sm" asChild className="gap-2">
                              <a href={deal.claim_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4" />
                                View Deal
                              </a>
                            </Button>
                          )}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
